import * as React from "react"
import { FaArrowRight, FaBars, FaCheck, FaEnvelope, FaPhoneAlt } from "react-icons/fa"
import TradeIcon from "../TradeIcon"
import QuoteForm from "./QuoteForm"
import { buildView } from "./view-model"
import "../../../styles/templates/base.css"
import "../../../styles/templates/studio.css"
import "../../../styles/templates/studio.mobile.css"
import "../../../styles/templates/port.css"

/**
 * Free Template 4 — the Mockup Maker's `studio` design, ported — MTC-FCS-041.
 * ============================================================================
 * Minimal editorial: serif display, square geometry, numbered sections, wide
 * letter-spaced labels. Same DOM and same stylesheet as
 * `source--mockup-maker/src/templates/studio`; studio.css is copied verbatim.
 *
 * ── THE NUMBERED LABELS ARE A SEQUENCE, SO THEY ARE COUNTED ────────────────
 * `01 — What we do`, `02 — How it works`… The original numbers whichever
 * sections actually render, which is the only version that stays correct when
 * a section drops out. Same here: the list is built first, then read.
 *
 * The review section is not ported — see TemplateClean.js for why a stand-in
 * testimonial cannot go on a real business's site.
 */

/**
 * ── LOCAL OR REMOTE — FCS-057(b) ───────────────────────────────────────────
 * DEFAULT_MODE is the switch this design runs in when nobody says otherwise,
 * and it is "local": the content is whatever the builder hands over, which is
 * the draft somebody is editing.
 *
 * In "remote" mode the same component draws a PUBLISHED site, and the JSON
 * fetched from Storage is the only source of anything — text, photographs,
 * contact details, colours. Nothing here reaches for a sample value in that
 * mode, because a placeholder on a real contractor's domain is a sentence
 * they never wrote on a website customers are reading. The refusing is done
 * before this point, in lib/site-content.js: a file that is missing a
 * required field never reaches a template at all.
 *
 * TEMPLATE_VERSION is what a published file is checked against, so a site
 * published against an older shape of this design is refused rather than
 * rendered wrong.
 */
export const DEFAULT_MODE = "local"
export const TEMPLATE_ID = "mm-studio"
export const TEMPLATE_VERSION = "1"

const Photo = ({ src, slot, alt }) =>
  src ? (
    <div className="mk-photo" data-slot={slot}>
      <img src={src} alt={alt || ""} />
    </div>
  ) : (
    <div className="mk-photo" data-slot={slot} aria-hidden="true" />
  )

const TemplateStudio = ({ draft, plan, mode = DEFAULT_MODE }) => {
  const v = buildView(draft, { plan, mode })

  const navItems = [
    { href: "#top", label: "Home" },
    { href: "#services", label: "Services" },
    ...(v.hasGallery ? [{ href: "#gallery", label: "Our Work" }] : []),
    ...(v.aboutText ? [{ href: "#about", label: "About" }] : []),
    { href: "#contact", label: "Contact" },
  ]

  // Numbered in the order they appear, and only the ones that appear.
  const labelled = [
    ...(v.slogan ? ["hero"] : []),
    "services",
    "process",
    ...(v.aboutText ? ["about"] : []),
    "contact",
  ]
  const num = id => String(labelled.indexOf(id) + 1).padStart(2, "0")

  const steps = [
    {
      title: "Get in touch",
      desc: v.hasPhone
        ? `Call ${v.phone} or send a message telling us what you need.`
        : "Send us a message telling us what you need.",
    },
    { title: "We reply", desc: "We come back to you about what you need." },
    { title: "Your quote", desc: "You hear back about the job and what it will cost." },
    { title: "The work", desc: "The job is booked in." },
  ]

  // ("Fixed written price" and "Tidy site" were drawn under every service for every contractor: nobody said so. FCS-124)
  const blockPoints = []

  const defs = [
    ...(v.hasPhone ? [{ term: "Telephone", value: v.phone }] : []),
    ...(v.hasEmail ? [{ term: "Email", value: v.email }] : []),
    ...(v.serviceArea ? [{ term: "Service area", value: v.serviceArea }] : []),
  ]

  const hasTopline = v.hasPhone || v.hasEmail

  return (
    <div className="mk mk-studio" style={v.style} data-mode="light" id="top">
      {hasTopline && (
        <div className="st-topline">
          <div className="st-container st-topline-inner">
            {v.hasPhone && (
              <span>
                <FaPhoneAlt /> {v.phone}
              </span>
            )}
            {v.hasEmail && (
              <span>
                <FaEnvelope /> {v.email}
              </span>
            )}
          </div>
        </div>
      )}

      <header className="st-header">
        <div className="st-container st-header-inner">
          <span className="st-wordmark">
            {v.lead} {v.accent && <em>{v.accent}</em>}
          </span>
          <nav className="st-nav">
            {navItems.map((link, i) => (
              <a key={link.label} href={link.href} className={i === 0 ? "is-active" : undefined}>
                {link.label}
              </a>
            ))}
          </nav>
          <a href="#contact" className="st-btn st-btn-solid">
            Request a quote
          </a>
          <span className="st-burger">
            <FaBars />
          </span>
        </div>
      </header>

      {/* ── HERO ───────────────────────────────── */}
      <section className="st-hero">
        <div className="st-container st-hero-inner">
          <div>
            {v.slogan && (
              <span className="st-label">
                <i>{num("hero")}</i> {v.slogan}
              </span>
            )}
            <h1 className="st-display st-hero-title">
              {v.trade}
              {v.cityIsReal ? <em> in {v.city}</em> : null}
            </h1>
            {v.aboutShort && <p className="st-hero-desc">{v.aboutShort}</p>}
            <div className="st-hero-actions">
              <a href={v.hasPhone ? v.phoneHref : "#contact"} className="st-btn st-btn-brand">
                {v.hasPhone ? v.phone : "Request a quote"} <FaArrowRight />
              </a>
              <a href="#services" className="st-btn st-btn-line">
                Our services
              </a>
            </div>
          </div>
          <div className="st-hero-media">
            <div className="st-hero-photo">
              <Photo src={v.heroImage} slot="hero" />
            </div>
            <span className="st-hero-block" />
          </div>
        </div>
      </section>

      {/* ── SERVICES ───────────────────────────── */}
      <section className="st-services" id="services">
        <div className="st-container">
          <div className="st-section-head">
            <div>
              <span className="st-label">
                <i>{num("services")}</i> What we do
              </span>
              <h2 className="st-display st-section-title">
                What we do
              </h2>
            </div>
            {v.serviceArea ? <p className="st-body">Covering {v.serviceArea}.</p> : null}
          </div>

          {v.services.map((service, i) => (
            <article className="st-block" key={service.slug}>
              <div className="st-block-media">
                <Photo src={service.image} slot={`service${i + 1}`} />
                {!service.image && (
                  <span className="st-block-glyph">
                    <TradeIcon name={service.icon} />
                  </span>
                )}
              </div>
              <div>
                <span className="st-block-num">{String(i + 1).padStart(2, "0")} —</span>
                <h3 className="st-display st-block-title">{service.title}</h3>
                <p className="st-block-desc">{service.description}</p>
                <div className="st-block-list">
                  {blockPoints.map(point => (
                    <span key={point}>
                      <FaCheck /> {point}
                    </span>
                  ))}
                </div>
                <a href="#contact" className="st-block-link">
                  Enquire about this <FaArrowRight />
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── PROCESS ────────────────────────────── */}
      <section className="st-process">
        <div className="st-container">
          <span className="st-label">
            <i>{num("process")}</i> How it works
          </span>
          <h2 className="st-display st-section-title" style={{ marginBottom: 44 }}>
            How it <em>works</em>
          </h2>
          <div className="st-process-grid">
            {steps.map((step, i) => (
              <div className="st-step" key={step.title}>
                <span className="st-step-num">{String(i + 1).padStart(2, "0")} —</span>
                <h3 className="st-step-title">{step.title}</h3>
                <p className="st-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUR WORK ───────────────────────────── */}
      {v.hasGallery && (
        <section className="st-work" id="gallery">
          <div className="st-container">
            <span className="st-label">Our work</span>
            <h2 className="st-display st-section-title" style={{ marginBottom: 44 }}>
              Recent <em>projects</em>
            </h2>
            <div className="st-work-grid">
              {v.gallery.slice(0, 6).map((url, i) => (
                <figure className="st-work-tile mk-tile" key={`${url}-${i}`}>
                  <Photo src={url} slot={`gallery${i + 1}`} />
                  <figcaption>{String(i + 1).padStart(2, "0")}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── STATEMENT ──────────────────────────── */}
      {v.aboutText && (
        <section className="st-statement" id="about">
          <div className="st-container">
            <span className="st-label">
              <i>{num("about")}</i> About us
            </span>
            <p className="st-statement-text">{v.aboutShort}</p>
            <div className="st-statement-by">
              <span className="mk-monogram">{v.initials}</span>
              <span className="st-statement-by-text">
                <strong>{v.name}</strong>
                {v.serviceArea && <span>{v.serviceArea}</span>}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* ── CONTACT ────────────────────────────── */}
      <section className="st-contact" id="contact">
        <div className={defs.length > 0 ? "st-container st-contact-grid" : "st-container"}>
          <div>
            <span className="st-label">
              <i>{num("contact")}</i> Get in touch
            </span>
            <h2 className="st-display st-contact-title">
              Tell us what you need
              <br />
              and we will <em>quote it</em>.
            </h2>
            {v.hasPhone && (
              <a href={v.phoneHref} className="st-contact-phone">
                {v.phone}
              </a>
            )}
            <p className="st-body" style={{ maxWidth: 460 }}>
              Tell us what you need and we will come back to you.
            </p>
          </div>
          {defs.length > 0 && (
            <dl className="st-defs">
              {defs.map(def => (
                <div className="st-def" key={def.term}>
                  <dt>{def.term}</dt>
                  <dd>{def.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </section>


      {/* ── REQUEST A QUOTE — MTC-FCS-017 ──────── */}
      {v.wantsForm && (
        <section className="mk-quote-section" id="quote" data-premium="leadform">
          <div className="mk-quote-inner">
            <div className="mk-quote-head">
              <h2 className="st-display st-section-title">Request a Quote</h2>
              <p className="mk-quote-lead">
                Tell us what you need and we will come back to you.
              </p>
            </div>
            <QuoteForm view={v} />
          </div>
        </section>
      )}
      {/* ── FOOTER ─────────────────────────────── */}
      <footer className="st-footer">
        <div className="st-container">
          <div className="st-footer-top">
            <p className="st-footer-cta">
              Ready when you are — <em>get a free quote today.</em>
            </p>
            <a href={v.hasPhone ? v.phoneHref : "#contact"} className="st-btn st-btn-brand">
              {v.hasPhone ? v.phone : "Request a quote"} <FaArrowRight />
            </a>
          </div>
          <div className="st-footer-cols">
            <div>
              <p className="st-footer-title">Company</p>
              <div className="st-footer-list">
                {navItems.map(link => (
                  <a key={link.label} href={link.href}>
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
            <div>
              <p className="st-footer-title">Services</p>
              <div className="st-footer-list">
                {v.services.map(service => (
                  <span key={service.slug}>{service.title}</span>
                ))}
              </div>
            </div>
            {defs.length > 0 && (
              <div>
                <p className="st-footer-title">Get in touch</p>
                <div className="st-footer-list">
                  {defs.map(def => (
                    <span key={def.term}>{def.value}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="st-footer-bottom">
            <span>
              © {v.year} {v.name}. All rights reserved.
            </span>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default TemplateStudio
